// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
// Namespace import, not named: @sentry/nextjs is CommonJS, and once the whole
// import chain is ESM cjs-module-lexer cannot see these as named exports.
import * as SentryNextJS from '@sentry/nextjs';

import { gqlValidationFingerprint } from '../../errors/gqlValidationFingerprint';
import {
  isExtensionMessagingRejection,
  isNavigationBlockedRejection,
  SENTRY_ALLOW_URLS,
  SENTRY_IGNORE_ERRORS,
  shouldSendErrorToSentry,
} from '../../errors/helpers';
import { sanitizeEvent } from '../../errors/sanitizeEvent';

type InitOptions = Partial<Parameters<typeof SentryNextJS.init>[0]> & {
  enableFeedback?: boolean;
  enableReplay?: boolean;
};

/**
 * Release version embedded into the JS bundle at build time (CEXP-21498).
 *
 * `process.env.RELEASE_VERSION` is inlined by Next's `env` config in
 * `@anchorage/next/server/config` from the deploy scripts' `VERSION` (the same
 * identifier `scripts/release.sh` creates the Sentry release under). Unlike
 * `window.env.SENTRY_RELEASE` — which the backend live-renders with the current
 * pod's SHA and is therefore wrong when a stale/cached bundle is served (root
 * cause of INC-6588) — this reflects the version the running code was actually
 * built from. Undefined outside deploy builds.
 */
export const getEmbeddedRelease = (): string | undefined =>
  process.env.RELEASE_VERSION || undefined;

const beforeSend: InitOptions['beforeSend'] = (event, hint) => {
  const err = hint?.originalException ?? null;

  if (!shouldSendErrorToSentry(err as string)) {
    return null;
  }

  // Intentional stopRouting() cancellation (packages/hooks), not a real error.
  // SENTRY_IGNORE_ERRORS can't match it — non-Error rejections get grouped under a
  // synthesized message, not the original name/message. (FRONTOFFICE-XF)
  if (isNavigationBlockedRejection(err)) {
    return null;
  }

  // A third-party browser extension's dead message channel, seen from the
  // page. Web apps only: the browser extension has its own config.
  if (
    isExtensionMessagingRejection(
      err,
      event.exception?.values?.[0]?.stacktrace?.frames,
    )
  ) {
    return null;
  }

  // Split GraphQL validation failures by the schema coordinates they name;
  // Apollo's single generic 400 message otherwise groups every breaking schema
  // change into one issue, which no alert can usefully target.
  const fingerprint = gqlValidationFingerprint(err);
  if (fingerprint) {
    event.fingerprint = fingerprint;
  }

  // Redact attacker-controlled free text (e.g. GraphQL variables) before the
  // event leaves the browser — Sentry events are fed into AI analysis, so free
  // text in the payload is a prompt-injection vector. See sanitizeEvent.
  return sanitizeEvent(event);
};

/**
 * Asynchronously initializes Sentry with optional feedback integration.
 * Use this when you want to conditionally load the feedback integration to save bundle size.
 *
 * @param options - Sentry initialization options
 * @param options.enableFeedback - Whether to load the feedback integration (default: true)
 * @returns Promise that resolves when Sentry is initialized
 *
 * @example
 * // With feedback integration
 * await asyncSentryInit();
 *
 * @example
 * // Without feedback integration (saves ~30-50KB gzipped)
 * await asyncSentryInit({ enableFeedback: false });
 */
export const asyncSentryInit = async ({
  dsn,
  environment,
  release,
  debug,
  enableFeedback = true,
  enableReplay = false,
  ...sentryInitConfig
}: InitOptions = {}) => {
  const embeddedRelease = getEmbeddedRelease();
  const DSN = dsn ?? window.env?.SENTRY_DSN;
  // Prefer the build-time embedded release over the backend-rendered
  // window.env.SENTRY_RELEASE so events are tagged with the version the loaded
  // bundle was built from, not the current pod's SHA (CEXP-21498 / INC-6588).
  const RELEASE = release ?? embeddedRelease ?? window.env?.SENTRY_RELEASE;
  const ENVIRONMENT = environment ?? window.env?.SENTRY_ENVIRONMENT;
  const DEBUG = debug ?? window.env?.SENTRY_DEBUG;

  // Expose the bundle's build-time version on window for diagnostics. Comparing
  // it against window.env.SENTRY_RELEASE (the live backend SHA) reveals when a
  // client is running stale/cached JS. Set unconditionally (before the
  // production gate) so it is available in every deploy build.
  if (typeof window !== 'undefined' && embeddedRelease) {
    window.__APP_VERSION__ = embeddedRelease;
  }

  // Do not initialize the Sentry instance with a DSN if you don't want to report
  // errors for that environment
  if (process.env.NODE_ENV === 'production') {
    if (!DSN) {
      console.error('no sentry dsn defined');
      return;
    }
    if (!RELEASE) {
      console.error('no sentry release defined');
      return;
    }
    if (!ENVIRONMENT) {
      console.error('no sentry environment defined');
      return;
    }

    const integrations = [];

    // Replay is opt-in per app. Buffer-only mode: nothing is sent on healthy
    // sessions, only sessions that fire an error get a replay attached.
    // PII conservative defaults: mask all text + block all media.
    if (enableReplay) {
      integrations.push(
        SentryNextJS.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      );
    }

    // Dynamically import feedbackIntegration only when needed
    if (enableFeedback) {
      // Use dynamic import to allow webpack to create a separate chunk
      const feedbackModule = await import(
        /* webpackChunkName: "sentry-feedback" */ '@sentry/nextjs'
      ).catch(
        /* istanbul ignore next - Defensive code, dynamic import failure is difficult to test */
        () => ({ feedbackIntegration: undefined }),
      );

      if (feedbackModule.feedbackIntegration) {
        integrations.push(
          feedbackModule.feedbackIntegration({
            // Disable injecting sentry's widget so we can show our own form
            autoInject: false,
            // Require a user email so we can get back to them
            requireUserEmail: true,
          }),
        );
      }
    }

    SentryNextJS.init({
      dsn: DSN,
      environment: ENVIRONMENT,
      release: RELEASE,
      // Add optional integrations for additional features
      // Replay + feedback integrations are populated above based on per-app
      // opt-in flags (enableReplay / enableFeedback).
      integrations,
      // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
      tracesSampleRate: 1,
      // Buffer-only replay mode: never record healthy sessions, attach the
      // last ~60s of buffer when an error fires. Cost scales with error
      // sessions, not total sessions.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // Setting this option to true will print useful information to the console while you're setting up Sentry.
      debug: !!DEBUG,
      // ignore errors that are not actionable
      ignoreErrors: SENTRY_IGNORE_ERRORS,
      // only report errors originating from our own domains
      allowUrls: SENTRY_ALLOW_URLS,
      // ignore errors that are not actionable
      beforeSend,
      // overrides
      ...sentryInitConfig,
    });
  }
};

/**
 * Synchronously initializes Sentry.
 * This is a backward-compatible wrapper that calls asyncSentryInit.
 * Initialization happens asynchronously in the background (fire and forget).
 *
 * @param options - Sentry initialization options
 * @param options.enableFeedback - Whether to load the feedback integration (default: true)
 *
 * @example
 * sentryInit(); // Fire and forget initialization with feedback
 *
 * @example
 * sentryInit({ enableFeedback: false }); // Without feedback (saves ~30-50KB gzipped)
 *
 */
export const sentryInit = (options?: InitOptions) => {
  // Fire and forget - maintains backward compatibility for synchronous calls
  void asyncSentryInit(options).catch((error) => {
    console.error('Failed to initialize Sentry:', error);
  });
};
